/**
 * Activity (glossary): the live feed of what happens in the chats in Scope.
 * The CRM sends every event the person may see; what counts is filtered
 * here, per person, so changing a filter is instant (ADR 0022).
 */

/** An event as the Workspace socket sends it (crm_workspace_events row). */
export interface WorkspaceEvent {
  id: string;
  account_id: string;
  fan_id: string;
  kind:
    | 'fan_message'
    | 'tip'
    | 'ppv_bought'
    | 'subscription'
    | 'handoff'
    | 'hot_enter'
    | 'ai_sent'
    | 'team_sent'
    | 'fan_lists';
  occurred_at: string;
  source: string;
  payload: Record<string, unknown> & {
    fan_name?: string | null;
    fan_avatar?: string | null;
    snippet?: string;
    has_media?: boolean;
    amount?: number | null;
    amount_label?: string | null;
    buying_power?: string | null;
    hot?: boolean;
    phase?: string | null;
    reason?: string;
    lists?: string[];
    ignored?: boolean;
    operator_id?: string;
    /** 'fansly' on Fansly events (ADR 0045); absent on OnlyFans. */
    platform?: string;
    /** Fansly: the thread's id. */
    group_id?: string | null;
    subscribed?: boolean;
    /** 'follow' when a Fansly follow is the New subscriber. */
    via?: string;
  };
}

export type ActivityKind = 'message' | 'ppv' | 'tip' | 'handoff' | 'hot' | 'list' | 'subscriber';
export const ACTIVITY_KINDS: ActivityKind[] = ['message', 'ppv', 'tip', 'handoff', 'hot', 'list', 'subscriber'];

/** Hand-off reason groups (design brief §4.1); codes come from the brain. */
export type HandoffGroup = 'asks' | 'mood' | 'media' | 'risk' | 'sales' | 'stuck' | 'opportunity' | 'other';
export const HANDOFF_GROUPS: Record<Exclude<HandoffGroup, 'other'>, string[]> = {
  asks: [
    'fan_asks_for_content',
    'fan_asks_about_catalog',
    'fan_wants_to_buy_content',
    'fan_wants_content',
    'content_request',
    'fan_asks_for_custom_content',
    'fan_asks_for_voice_video_call',
    'fan_asks_how_model_looks_now',
    'fan_asks_for_verification_photo',
    'fan_asks_to_see_body_part',
    'fan_asks_about_pricing',
    'fan_asks_off_platform_or_external_payment',
    'fan_offers_external_payment',
    'off_platform',
  ],
  mood: ['fan_not_happy', 'fan_suspects_ai_or_fake', 'fan_no_money', 'fan_busy', 'fan_disengaged'],
  media: ['fan_sent_media_file', 'fan_awaiting_media'],
  risk: ['fan_mentions_banned_platform_topic', 'fan_message_involves_banned_platform_topic', 'fan_mentions_other_model_page'],
  sales: [
    'fan_agreed_to_buy_ppv_offer',
    'fan_agreed_to_tip',
    'fan_agreed_to_vip',
    'fan_does_not_agree_to_buy_ppv',
    'fan_does_not_agree_to_tip',
    'fan_does_not_agree_to_vip',
  ],
  stuck: ['no_content_available', 'no_pricing_set', 'live_custom_request', 'engine_error', 'turn_threshold'],
  opportunity: ['teasing_content_opportunity', 'sales_opportunity'],
};
export const HANDOFF_GROUP_IDS: HandoffGroup[] = ['asks', 'mood', 'media', 'risk', 'sales', 'stuck', 'opportunity', 'other'];

const GROUP_OF = new Map<string, HandoffGroup>(
  Object.entries(HANDOFF_GROUPS).flatMap(([group, codes]) => codes.map((code) => [code, group as HandoffGroup])),
);

export function handoffGroup(reason: string | undefined): HandoffGroup {
  return GROUP_OF.get(String(reason || '')) ?? 'other';
}

export type HotMode = 'change' | 'every';

export interface ActivitySettings {
  kinds: Record<ActivityKind, boolean>;
  /** Fan messages only from fans at or above this buying power (0 = everyone). */
  minBuyingPower: number;
  includeUnknownBp: boolean;
  /** Purchases and tips below this amount are left out. */
  minAmount: number;
  handoffGroups: Record<HandoffGroup, boolean>;
  hotMode: HotMode;
  /** OnlyFans list names watched per Account. */
  watchedLists: Record<string, string[]>;
  /** Fans the AI ignores because of their lists, on every Account. Off by default: it is mostly spam. */
  showAiIgnored: boolean;
}

export const DEFAULT_ACTIVITY_SETTINGS: ActivitySettings = {
  kinds: { message: true, ppv: true, tip: true, handoff: true, hot: true, list: true, subscriber: true },
  minBuyingPower: 3,
  includeUnknownBp: true,
  minAmount: 0,
  handoffGroups: { asks: true, mood: true, media: true, risk: true, sales: true, stuck: true, opportunity: true, other: true },
  hotMode: 'change',
  watchedLists: {},
  showAiIgnored: false,
};

/** Activity keeps about the last two hours. */
export const ACTIVITY_WINDOW_MS = 2 * 60 * 60 * 1000;

export interface ActivityItem {
  id: string;
  kind: ActivityKind;
  event: WorkspaceEvent;
  atMs: number;
  /** List items: the watched lists the fan is on, or 'ignored' when it shows because the AI ignores the fan. */
  lists?: string[] | 'ignored';
}

function amountOk(ev: WorkspaceEvent, min: number): boolean {
  if (!min) return true;
  const n = Number(ev.payload.amount);
  return Number.isFinite(n) && n >= min;
}

function bpOk(ev: WorkspaceEvent, s: ActivitySettings): boolean {
  const raw = ev.payload.buying_power;
  const n = raw == null || raw === '' ? NaN : Number(raw);
  if (!Number.isFinite(n)) return s.includeUnknownBp;
  return n >= s.minBuyingPower;
}

/** Why a list event shows: the watched lists the fan is on, 'ignored' (AI ignores the fan, opted in for that Account), or null. */
function listMatch(ev: WorkspaceEvent, s: ActivitySettings): string[] | 'ignored' | null {
  const watched = s.watchedLists[ev.account_id];
  if (watched?.length) {
    const lists = new Set((ev.payload.lists || []).map((l) => String(l).toLowerCase()));
    const hit = watched.filter((l) => lists.has(l.toLowerCase()));
    if (hit.length) return hit;
  }
  return ev.payload.ignored && s.showAiIgnored ? 'ignored' : null;
}

/** What Activity shows for one event under these settings, or null. */
function kindFor(ev: WorkspaceEvent, s: ActivitySettings): ActivityKind | null {
  switch (ev.kind) {
    case 'fan_message':
      if (ev.payload.hot && s.hotMode === 'every') return s.kinds.hot ? 'hot' : null;
      return s.kinds.message && bpOk(ev, s) ? 'message' : null;
    case 'tip':
      return s.kinds.tip && amountOk(ev, s.minAmount) ? 'tip' : null;
    case 'ppv_bought':
      return s.kinds.ppv && amountOk(ev, s.minAmount) ? 'ppv' : null;
    case 'handoff':
      return s.kinds.handoff && s.handoffGroups[handoffGroup(ev.payload.reason)] ? 'handoff' : null;
    case 'hot_enter':
      return s.kinds.hot && s.hotMode === 'change' ? 'hot' : null;
    case 'fan_lists':
      return s.kinds.list && listMatch(ev, s) ? 'list' : null;
    // New subscriber (glossary): a subscription on OnlyFans, a follow on Fansly; an expiry is not one.
    case 'subscription':
      return s.kinds.subscriber && ev.payload.subscribed !== false ? 'subscriber' : null;
    default:
      return null;
  }
}

/**
 * Activity items, newest first: events from the last two hours that pass the
 * person's filters and were not dismissed (dismissals map event id → expiry).
 */
export function activityItems(
  events: WorkspaceEvent[],
  settings: ActivitySettings,
  dismissed: Record<string, number>,
  nowMs: number,
): ActivityItem[] {
  const since = nowMs - ACTIVITY_WINDOW_MS;
  const seen = new Set<string>();
  const out: ActivityItem[] = [];
  for (const ev of events) {
    if (seen.has(ev.id)) continue;
    seen.add(ev.id);
    const atMs = Date.parse(ev.occurred_at);
    if (!Number.isFinite(atMs) || atMs < since) continue;
    if ((dismissed[ev.id] ?? 0) > nowMs) continue;
    const kind = kindFor(ev, settings);
    if (kind === 'list') out.push({ id: ev.id, kind, event: ev, atMs, lists: listMatch(ev, settings) ?? undefined });
    else if (kind) out.push({ id: ev.id, kind, event: ev, atMs });
  }
  return out.sort((a, b) => b.atMs - a.atMs || Number(b.id) - Number(a.id));
}

function bools<K extends string>(defaults: Record<K, boolean>, raw: unknown): Record<K, boolean> {
  const out = { ...defaults };
  if (raw && typeof raw === 'object') {
    for (const k of Object.keys(defaults) as K[]) {
      const v = (raw as Record<string, unknown>)[k];
      if (typeof v === 'boolean') out[k] = v;
    }
  }
  return out;
}

/** Activity settings saved in Workspace preferences, repaired against the defaults. */
export function activitySettingsFrom(raw: unknown): ActivitySettings {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const d = DEFAULT_ACTIVITY_SETTINGS;
  const num = (v: unknown, fallback: number, max: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(0, v)) : fallback);
  const watched: Record<string, string[]> = {};
  if (r.watchedLists && typeof r.watchedLists === 'object') {
    for (const [acc, names] of Object.entries(r.watchedLists as Record<string, unknown>)) {
      if (Array.isArray(names)) watched[acc] = names.filter((n): n is string => typeof n === 'string' && !!n);
    }
  }
  return {
    kinds: bools(d.kinds, r.kinds),
    minBuyingPower: Math.round(num(r.minBuyingPower, d.minBuyingPower, 5)),
    includeUnknownBp: typeof r.includeUnknownBp === 'boolean' ? r.includeUnknownBp : d.includeUnknownBp,
    minAmount: num(r.minAmount, d.minAmount, 100000),
    handoffGroups: bools(d.handoffGroups, r.handoffGroups),
    hotMode: r.hotMode === 'every' ? 'every' : 'change',
    watchedLists: watched,
    // New key on purpose: the old `aiIgnoredLists` was on by default and got saved with every filter change.
    showAiIgnored: r.showAiIgnored === true,
  };
}
